import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Send, CheckCircle2, XCircle, RotateCcw, ArrowRight } from "lucide-react";
import Sidebar from "../components/Sidebar";
import TopNav from "../components/TopNav";
import { useProgress } from "../contexts/ProgressContext";
import { useGetModulesQuery } from "../redux/Clinictrainingapi";

export default function Quiz() {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const { completeQuiz, completeLesson } = useProgress();

  const { data: modules, isLoading, isError } = useGetModulesQuery();
  const [answers, setAnswers] = useState({});       // { [questionId]: optionLabel }
  const [revealed, setRevealed] = useState({});     // { [questionId]: true } once answered

  // Always scroll to top when quiz loads
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [moduleId]);

  if (isLoading) return <div className="p-8">Loading...</div>;
  if (isError) return <div className="p-8">Error loading quiz</div>;

  const module = modules?.find((m) => m.order === Number(moduleId));
  if (!module) return <div className="p-8">Module not found</div>;

  const questions = module.knowledge_checks ?? [];
  if (!questions.length)
    return <div className="p-8">No quiz for this module</div>;

  const answeredCount = Object.keys(answers).length;
  const allAnswered = answeredCount === questions.length;

  // Compute live score
  const score = questions.reduce(
    (acc, q) => acc + (answers[q.id] === q.correct_answer ? 1 : 0),
    0
  );
  const passingThreshold = Math.ceil(questions.length * 0.6); // 60%
  const didPass = score >= passingThreshold;

  const handleSelect = (questionId, optionLabel) => {
    // Only allow selecting once per question
    if (revealed[questionId]) return;
    setAnswers((prev) => ({ ...prev, [questionId]: optionLabel }));
    setRevealed((prev) => ({ ...prev, [questionId]: true }));
  };

  const handleSubmit = () => {
    (module.sections ?? []).forEach((s) =>
      completeLesson(module.order, s.order, { moduleDbId: module.id, sectionDbId: s.id }),
    );
    completeQuiz(module.order, score, questions.length);
    navigate(`/module/${module.order}/complete`);
  };

  const handleReviewModule = () => {
    // Navigate back to the first lesson of this module
    const sections = [...(module.sections ?? [])].sort((a, b) => a.order - b.order);
    if (sections.length > 0) {
      navigate(`/module/${module.order}/lesson/${sections[0].order}`);
    } else {
      navigate(`/module/${module.order}`);
    }
  };

  // Option button — no background changes, just cursor
  const getOptionStyle = (q) => {
    const isRevealed = revealed[q.id];
    return isRevealed
      ? "border-[#F1F5F9] bg-white cursor-default"
      : "border-[#F1F5F9] bg-white hover:border-gray-300 cursor-pointer";
  };

  // Radio circle border color
  const getRadioStyle = (q, opt) => {
    const isRevealed = revealed[q.id];
    const isSelected = answers[q.id] === opt.label;
    const isCorrect = opt.label === q.correct_answer;

    if (!isRevealed) return isSelected ? "border-primary" : "border-gray-300";
    if (isSelected && isCorrect) return "border-emerald-500";   // correct pick
    if (isSelected && !isCorrect) return "border-red-400";      // wrong pick
    return "border-gray-300";                                    // untouched
  };

  // Radio dot fill color (only shown on the selected option)
  const getRadioDotStyle = (q, opt) => {
    const isRevealed = revealed[q.id];
    const isSelected = answers[q.id] === opt.label;
    const isCorrect = opt.label === q.correct_answer;

    if (!isRevealed || !isSelected) return "bg-primary";
    if (isCorrect) return "bg-emerald-500";
    return "bg-red-400";
  };

  // Only show the dot on the option the user actually selected
  const showRadioDot = (q, opt) => answers[q.id] === opt.label;

  // Bottom caption based on score
  const getBottomCaption = () => {
    if (!allAnswered) return null;
    if (didPass) {
      return {
        text: `Great work! You scored ${score}/${questions.length}. Continue to complete the module.`,
        color: "text-emerald-600",
      };
    }
    return {
      text: `You scored ${score}/${questions.length}. We recommend reviewing the module before moving forward.`,
      color: "text-amber-600",
    };
  };

  const caption = getBottomCaption();

  return (
    <div className="min-h-screen bg-surface">
      <Sidebar />
      <div className="md:ml-16">
        <TopNav
          backTo={`/module/${module.order}`}
          backLabel="BACK"
          breadcrumbs={[`MODULE ${module.order}`, "KNOWLEDGE CHECK"]}
          rightContent={
            <span className="flex items-center gap-1.5 text-xs text-primary font-medium">
              {answeredCount > 0 ? answeredCount : 1}/{questions.length}
            </span>
          }
        />

        <div className="max-w-xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <motion.div
            className="mb-6 sm:mb-10"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="text-xs font-bold text-primary tracking-widest uppercase mb-1.5 sm:mb-2">
              Knowledge Check
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">
              Module {module.order} Quiz
            </h1>
            <p className="text-xs sm:text-sm text-gray-400">{module.title}</p>
          </motion.div>

          <div className="space-y-6 sm:space-y-12">
            {questions.map((q, qi) => {
              const isRevealed = revealed[q.id];
              const userAnswer = answers[q.id];
              const isCorrect = userAnswer === q.correct_answer;

              return (
                <motion.div
                  key={q.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.1 + qi * 0.08 }}
                  className="bg-white rounded-xl border border-[#F1F5F980] p-4 sm:p-6"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-bold text-primary tracking-widest uppercase">
                      QUESTION {qi + 1}
                    </div>

                    {/* Result badge after answering */}
                    <AnimatePresence>
                      {isRevealed && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.8 }}
                          transition={{ type: "spring", stiffness: 400, damping: 20 }}
                          className={`flex items-center gap-1 sm:gap-1.5 text-xs font-semibold px-2 sm:px-2.5 py-1 rounded-full ${
                            isCorrect
                              ? "bg-emerald-50 text-emerald-600"
                              : "bg-red-50 text-red-500"
                          }`}
                        >
                          {isCorrect ? (
                            <>
                              <CheckCircle2 size={13} />
                              Correct
                            </>
                          ) : (
                            <>
                              <XCircle size={13} />
                              Incorrect
                            </>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  <h3 className="text-sm sm:text-base font-light text-gray-900 mb-3 sm:mb-4">
                    {q.question}
                  </h3>

                  <div className="space-y-2">
                    {(q.options ?? [])
                      .filter((opt) => opt.text)
                      .map((opt) => {
                        return (
                          <motion.button
                            key={opt.label}
                            onClick={() => handleSelect(q.id, opt.label)}
                            whileTap={!revealed[q.id] ? { scale: 0.985 } : {}}
                            className={`w-full text-left px-3 sm:px-4 py-2.5 sm:py-3.5 rounded-xl border-2 transition-all flex items-center gap-2.5 sm:gap-3 ${getOptionStyle(q)}`}
                          >
                            <div
                              className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${getRadioStyle(q, opt)}`}
                            >
                              {showRadioDot(q, opt) && (
                                <motion.div
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  className={`w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full ${getRadioDotStyle(q, opt)}`}
                                />
                              )}
                            </div>
                            <span className="text-xs font-bold text-gray-400 w-4 sm:w-5 shrink-0">
                              {opt.label}
                            </span>
                            <span className="text-xs sm:text-sm text-gray-700">
                              {opt.text}
                            </span>
                          </motion.button>
                        );
                      })}
                  </div>

                  {/* Explanation nudge for wrong answer */}
                  <AnimatePresence>
                    {isRevealed && !isCorrect && (
                      <motion.div
                        initial={{ opacity: 0, height: 0, marginTop: 0 }}
                        animate={{ opacity: 1, height: "auto", marginTop: 12 }}
                        exit={{ opacity: 0, height: 0, marginTop: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="text-xs text-red-500 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                          The correct answer is{" "}
                          <span className="font-bold">{q.correct_answer}</span>.
                          {q.explanation && (
                            <span className="text-red-400 ml-1">{q.explanation}</span>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>

          {/* Score caption */}
          <AnimatePresence>
            {caption && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mt-6 sm:mt-8"
              >
                <div className={`text-center text-xs sm:text-sm font-medium ${caption.color} bg-white rounded-xl border border-gray-100 px-4 py-3`}>
                  {caption.text}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="mt-4 sm:mt-6 pb-6 sm:pb-8 flex flex-col sm:flex-row items-center gap-4 sm:justify-between">
            <div className="flex items-center gap-2 order-2 sm:order-1">
              <div className="flex gap-1">
                {questions.map((q, i) => {
                  const isRevealed = revealed[q.id];
                  const isCorrect = answers[q.id] === q.correct_answer;
                  return (
                    <div
                      key={i}
                      className={`h-1.5 rounded-full transition-all ${
                        isRevealed
                          ? isCorrect
                            ? "w-5 bg-emerald-400"
                            : "w-5 bg-red-400"
                          : answers[q.id] !== undefined
                          ? "w-5 bg-primary"
                          : "w-1.5 bg-gray-200"
                      }`}
                    />
                  );
                })}
              </div>
              <span className="text-xs text-gray-400 ml-2">
                {answeredCount}/{questions.length} COMPLETE
              </span>
            </div>

            <div className="flex items-center gap-2.5 order-1 sm:order-2 w-full sm:w-auto">
              {/* Review Module button — only when all answered and score is low */}
              <AnimatePresence>
                {allAnswered && !didPass && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    onClick={handleReviewModule}
                    className="flex items-center gap-2 border-2 border-amber-400 text-amber-600 hover:bg-amber-50 px-4 sm:px-5 py-2.5 rounded-full font-semibold text-xs sm:text-sm transition-all cursor-pointer flex-1 sm:flex-none justify-center"
                  >
                    <RotateCcw size={14} />
                    Review Module
                  </motion.button>
                )}
              </AnimatePresence>

              {/* Submit / Continue button */}
              <button
                onClick={handleSubmit}
                disabled={!allAnswered}
                className={`flex items-center gap-2 px-5 sm:px-6 py-2.5 rounded-full font-semibold text-xs sm:text-sm transition-all cursor-pointer shadow-lg disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none flex-1 sm:flex-none justify-center ${
                  allAnswered && !didPass
                    ? "bg-gray-500 hover:bg-gray-600 text-white shadow-gray-500/20"
                    : "bg-gradient-to-r from-primary to-cyan-400 hover:from-primary-dark hover:to-primary text-white shadow-primary/20"
                }`}
              >
                {allAnswered && !didPass ? (
                  <>
                    Continue Anyway
                    <ArrowRight size={14} />
                  </>
                ) : (
                  <>
                    Submit Answers
                    <Send size={14} />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
